/**
 * UserPicker: search the people directory and choose one or more people (plan
 * docs/plans/lms-plan.md 8.2 "a picker over the network's users listing name,
 * email, platform role, sites, with search and multi-select", 4.9, G14;
 * WP-43; reused by WP-45 and WP-46).
 *
 * Props (stable):
 *   value        string[]                                          the chosen user ids
 *   onChange     (ids: string[], people: DirectoryPerson[]) => void  people = the chosen ones the picker has seen
 *   excludeIds?  string[]      people listed but not choosable (for example a group's current members)
 *   excludedNote? string       the word shown beside them (default "Already added")
 *   siteId?      string | null narrow the directory to one site the admin reaches
 *   label?       string        the fieldset legend (default "People")
 *   pageSize?    number        rows per page (default 50, at most 200)
 *   disabled?    boolean
 *
 * Reads GET /admin/directory (api/directory.ts listDirectory): the people who
 * reach a site the acting admin reaches, nobody else (G14). The search box
 * matches a name or an email; typing waits SEARCH_DEBOUNCE_MS before asking,
 * clearing it asks at once. "Show more people" pages on. The chosen people
 * sit above the list as chips with a Remove button, so a choice survives a new
 * search. Each person's checkbox carries data-testid="person-option-<id>".
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2, X } from 'lucide-react';
import SearchInput from '../../../ui/SearchInput';
import { secondaryBtnCls } from '../../../ui/styles';
import { apiErrorMessage } from '../../api/client';
import { listDirectory } from '../../api/directory';
import type { DirectoryPerson } from '../../api/directory';
import { checkboxCls, checkRowCls, ErrorNote, metaCls, microLabelCls, Pill, personName, roleLabel } from './shared';

export interface UserPickerProps {
  value: string[];
  onChange: (ids: string[], people: DirectoryPerson[]) => void;
  excludeIds?: string[];
  excludedNote?: string;
  siteId?: string | null;
  label?: string;
  pageSize?: number;
  disabled?: boolean;
}

/** How long typing waits before the directory is asked (a search per keystroke would flood it). */
export const SEARCH_DEBOUNCE_MS = 250;

/** services/lms/groups/validators.js SEARCH_MAX: the server refuses a longer search. */
const SEARCH_MAX = 100;

export function UserPicker({
  value,
  onChange,
  excludeIds,
  excludedNote = 'Already added',
  siteId = null,
  label = 'People',
  pageSize = 50,
  disabled = false,
}: UserPickerProps) {
  const [q, setQ] = useState('');
  const [people, setPeople] = useState<DirectoryPerson[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Every person the picker has shown, so a chosen person keeps a name after a new search.
  const [known, setKnown] = useState<Record<string, DirectoryPerson>>({});
  const request = useRef(0);
  const limit = Math.max(1, Math.min(pageSize, 200));

  const fetchPage = useCallback(
    (term: string, offset: number) => {
      const id = ++request.current;
      setLoading(true);
      setError(null);
      listDirectory({ q: term, siteId, limit, offset })
        .then((page) => {
          if (id !== request.current) return;
          const items = Array.isArray(page.items) ? page.items : [];
          setPeople((prev) => (offset === 0 ? items : [...prev, ...items.filter((p) => !prev.some((x) => x.user_id === p.user_id))]));
          setHasMore(Boolean(page.page?.hasMore));
          setKnown((prev) => {
            const next = { ...prev };
            for (const p of items) next[p.user_id] = p;
            return next;
          });
        })
        .catch((err) => {
          if (id !== request.current) return;
          setError(apiErrorMessage(err, 'Could not load the people directory.'));
        })
        .finally(() => {
          if (id === request.current) setLoading(false);
        });
    },
    [siteId, limit]
  );

  useEffect(() => {
    const term = q.trim().slice(0, SEARCH_MAX);
    if (!term) {
      fetchPage('', 0);
      return undefined;
    }
    const timer = window.setTimeout(() => fetchPage(term, 0), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [q, fetchPage]);

  const excluded = new Set(excludeIds ?? []);

  const emit = (ids: string[]) => {
    onChange(
      ids,
      ids.map((id) => known[id]).filter((p): p is DirectoryPerson => Boolean(p))
    );
  };

  const toggle = (id: string) => {
    emit(value.includes(id) ? value.filter((v) => v !== id) : [...value, id]);
  };

  return (
    <fieldset data-testid="user-picker" disabled={disabled} className="min-w-0">
      <legend className={`${microLabelCls} mb-2`}>{label}</legend>

      {value.length > 0 && (
        <ul aria-label="Chosen people" className="mb-2 flex flex-wrap gap-1.5">
          {value.map((id) => {
            const name = known[id] ? personName(known[id]) : 'Chosen person';
            return (
              <li
                key={id}
                data-testid={`person-chip-${id}`}
                className="inline-flex items-center gap-1 rounded-full border border-primary-600 bg-primary-50 py-0.5 pl-2.5 pr-1 text-xs font-medium text-primary-700 dark:bg-primary-900/20 dark:text-primary-400"
              >
                {name}
                <button
                  type="button"
                  aria-label={`Remove ${name}`}
                  onClick={() => toggle(id)}
                  className="inline-flex h-11 w-11 items-center justify-center rounded-full focus:outline-none focus:ring-2 focus:ring-primary-500 sm:h-5 sm:w-5"
                >
                  <X className="h-3 w-3" />
                </button>
              </li>
            );
          })}
        </ul>
      )}

      <SearchInput value={q} onChange={setQ} placeholder="Search by name or email" label={`Search ${label.toLowerCase()}`} />

      {error && (
        <div className="mt-2">
          <ErrorNote>
            {error}{' '}
            <button type="button" className="font-medium underline" onClick={() => fetchPage(q.trim(), 0)}>
              Retry
            </button>
          </ErrorNote>
        </div>
      )}

      {!error && !loading && people.length === 0 && (
        <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
          {q.trim() ? "Nobody matches that search." : "Nobody at the sites you reach yet."}
        </p>
      )}

      {people.length > 0 && (
        <ul className="mt-2 max-h-72 divide-y divide-gray-100 overflow-y-auto rounded-lg border border-gray-200 dark:divide-gray-700 dark:border-gray-700">
          {people.map((p) => {
            const already = excluded.has(p.user_id);
            const sites = (p.sites ?? []).map((s) => s.name).filter(Boolean).join(', ');
            return (
              <li key={p.user_id}>
                <label className={`${checkRowCls} ${already ? 'cursor-default opacity-60' : ''}`}>
                  <input
                    type="checkbox"
                    className={checkboxCls}
                    checked={already || value.includes(p.user_id)}
                    disabled={already}
                    onChange={() => toggle(p.user_id)}
                    data-testid={`person-option-${p.user_id}`}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-medium text-gray-900 dark:text-gray-100">{personName(p)}</span>
                    <span className={`${metaCls} block truncate`}>
                      {[p.email, roleLabel(p.role), sites].filter(Boolean).join(' \u00b7 ')}
                    </span>
                  </span>
                  {already && <span className={metaCls}>{excludedNote}</span>}
                  {!already && p.is_active === false && <Pill tone="gray">Deactivated</Pill>}
                </label>
              </li>
            );
          })}
        </ul>
      )}

      <div className="mt-2 flex items-center gap-2">
        {loading && <Loader2 aria-label="Loading people" className="h-4 w-4 animate-spin text-primary-600 dark:text-primary-400" />}
        {hasMore && !loading && (
          <button type="button" className={secondaryBtnCls} onClick={() => fetchPage(q.trim(), people.length)}>
            Show more people
          </button>
        )}
      </div>
    </fieldset>
  );
}

export default UserPicker;
